import type { PageHelp } from '../../help/types';

/** Page guide for `/projects/:code/linked-keys` — keys of other projects this one exports (spec §3.2 rule 11, ADR 0006). */
export const keyLinksHelp: PageHelp = {
  title: 'Linked keys',
  intro:
    'Keys that belong to another project and that this project exports as well. When this application renders a key another application owns, link it instead of copying its text: the owner keeps translating it, and this project’s bundle carries the owner’s text.',
  sections: ['The list', 'Linking', 'Link dialog'],
  contexts: {
    'link-dialog': 'Click “Link keys” at the top of the page.',
    'link-dialog-paste': 'Click “Link keys” at the top of the page, then the “Paste names” tab.',
  },
  tips: [
    'A linked key ships in the locales its owner carries, and only once the owner has a source text for it. In a locale the owner does not carry, the application falls back as it does for any missing value.',
    'Edits of a linked key made in this project — in the keys grid or the in-context editor — are written directly only for people who may translate in the owner project. Everyone else’s edit becomes a suggestion the owner decides on its Suggestions page.',
    'Package keys are not linked: subscribe to the package in the project settings. A key the missing-keys inbox reports can be linked from there with “Use from …”.',
    'The owner sees which projects use a key, and renaming or deleting it asks for confirmation first, because the code of those projects still asks for the old name.',
  ],
  steps: [
    {
      selector: '[data-help="key-links-filters"]',
      title: 'Find a linked key',
      body: 'The number on the right is how many keys this project links. The list is sorted by key name and loads 100 at a time.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Einen verknüpften Schlüssel finden',
          body: 'Die Zahl rechts gibt an, wie viele Schlüssel dieses Projekt verknüpft. Die Liste ist nach Schlüsselname sortiert und lädt jeweils 100 auf einmal.',
        },
        'sr-Latn-RS': {
          title: 'Pronalaženje povezanog ključa',
          body: 'Broj desno pokazuje koliko ključeva ovaj projekat povezuje. Lista je sortirana po imenu ključa i učitava po 100 odjednom.',
        },
      },
    },
    {
      selector: '[data-help="key-links-search"]',
      title: 'Search by name',
      body: 'Filters the list by part of the key name, ignoring case. % and _ are searched as ordinary characters.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Nach Namen suchen',
          body: 'Filtert die Liste nach einem Teil des Schlüsselnamens, ohne Groß- und Kleinschreibung zu unterscheiden. % und _ werden als gewöhnliche Zeichen gesucht.',
        },
        'sr-Latn-RS': {
          title: 'Pretraga po imenu',
          body: 'Filtrira listu po delu imena ključa, bez razlike između velikih i malih slova. % i _ se traže kao obični znakovi.',
        },
      },
    },
    {
      selector: '[data-help="key-links-list"]',
      title: 'Read a link',
      body: 'Each row is one key of another project: its name, the project that owns it, the owner’s text in its source locale (the “other” form of a plural), and who linked it when. This is the text the application renders in that locale; the other locales come from the owner’s translations.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Eine Verknüpfung lesen',
          body: 'Jede Zeile ist ein Schlüssel eines anderen Projekts: sein Name, das Projekt, dem er gehört, der Text des Owners in dessen Ausgangssprache (bei einem Plural die Form „other“) und wer ihn wann verknüpft hat. Diesen Text zeigt die Anwendung in dieser Sprache; die übrigen Sprachen kommen aus den Übersetzungen des Owners.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje veze',
          body: 'Svaki red je jedan ključ drugog projekta: njegovo ime, projekat kome pripada, tekst vlasnika na njegovom izvornom jeziku (kod množine oblik „other“) i ko ga je i kada povezao. Taj tekst aplikacija prikazuje na tom jeziku; ostali jezici dolaze iz prevoda vlasnika.',
        },
      },
    },
    {
      selector: '[data-help="key-links-owner"]',
      title: 'The owner',
      body: 'The code of the project that owns the key; hover for its name. Translations of the key are made there, and suggestions from this project are decided there.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Der Owner',
          body: 'Der Code des Projekts, dem der Schlüssel gehört; mit dem Mauszeiger darüber sehen Sie seinen Namen. Dort wird der Schlüssel übersetzt, und dort werden Vorschläge aus diesem Projekt entschieden.',
        },
        'sr-Latn-RS': {
          title: 'Vlasnik',
          body: 'Kod projekta kome ključ pripada; zadržite pokazivač iznad njega da vidite naziv. Ključ se tamo prevodi, i tamo se odlučuje o predlozima iz ovog projekta.',
        },
      },
    },
    {
      selector: '[data-help="key-links-unlink"]',
      title: 'Unlink a key',
      body: 'Asks for confirmation, then takes the key out of this project’s bundle on the next publish; wherever the application renders it, it falls back. The owner keeps the key. Needs the owner or developer role on this project, or an administrator.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Eine Verknüpfung lösen',
          body: 'Fragt nach einer Bestätigung und nimmt den Schlüssel dann beim nächsten Veröffentlichen aus dem Bundle dieses Projekts; wo die Anwendung ihn anzeigt, greift der Fallback. Der Owner behält den Schlüssel. Dafür brauchen Sie die Rolle Owner oder Developer in diesem Projekt oder Administratorrechte.',
        },
        'sr-Latn-RS': {
          title: 'Uklanjanje veze',
          body: 'Traži potvrdu, a zatim ključ izlazi iz bundle-a ovog projekta pri sledećoj objavi; gde ga aplikacija prikazuje, prelazi na rezervnu vrednost. Vlasnik zadržava ključ. Potrebna je uloga owner ili developer na ovom projektu, ili administrator.',
        },
      },
    },
    {
      selector: '[data-help="key-links-load-more"]',
      title: 'Load the next links',
      body: 'Shown when more links exist than are loaded. Each click loads up to 100 more.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Weitere Verknüpfungen laden',
          body: 'Erscheint, wenn es mehr Verknüpfungen gibt als geladen sind. Jeder Klick lädt bis zu 100 weitere.',
        },
        'sr-Latn-RS': {
          title: 'Učitavanje sledećih veza',
          body: 'Prikazuje se kada postoji više veza nego što je učitano. Svaki klik učitava još do 100.',
        },
      },
    },
    {
      selector: '[data-help="key-links-add"]',
      title: 'Link keys',
      body: 'Opens the dialog for linking keys of other projects: picked from a search, or pasted by name. Needs the owner or developer role on this project, or an administrator; the owner project is not asked.',
      section: 'Linking',
      i18n: {
        'de-AT': {
          title: 'Schlüssel verknüpfen',
          body: 'Öffnet den Dialog, in dem Sie Schlüssel anderer Projekte verknüpfen: aus einer Suche ausgewählt oder über ihren Namen eingefügt. Dafür brauchen Sie die Rolle Owner oder Developer in diesem Projekt oder Administratorrechte; das Owner-Projekt wird nicht gefragt.',
        },
        'sr-Latn-RS': {
          title: 'Povezivanje ključeva',
          body: 'Otvara dijalog za povezivanje ključeva drugih projekata: izabranih iz pretrage ili nalepljenih po imenu. Potrebna je uloga owner ili developer na ovom projektu, ili administrator; projekat-vlasnik se ne pita.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-mode"]',
      title: 'Search or paste',
      body: '“Search” finds keys and lets you tick them; “Paste names” takes a list of names, for example copied from the code. What you pick in one tab is kept when you switch, and “Link” sends both together.',
      section: 'Link dialog',
      context: 'link-dialog',
      i18n: {
        'de-AT': {
          title: 'Suchen oder einfügen',
          body: '„Search“ findet Schlüssel und lässt Sie sie ankreuzen; „Paste names“ nimmt eine Liste von Namen, etwa aus dem Code kopiert. Was Sie in einem Reiter auswählen, bleibt beim Wechseln erhalten, und „Link“ sendet beides gemeinsam.',
        },
        'sr-Latn-RS': {
          title: 'Pretraga ili lepljenje',
          body: '„Search“ pronalazi ključeve i dozvoljava da ih čekirate; „Paste names“ prima listu imena, na primer kopiranu iz koda. Ono što izaberete u jednom tabu ostaje kada pređete na drugi, a „Link“ šalje oba zajedno.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-search"]',
      title: 'Search the keys of other projects',
      body: 'Searches key names, descriptions and the owner’s source text in every other project, including projects you cannot open yourself, because linking needs no right in the owner. The search starts after two characters, once you stop typing. This project’s own keys and package keys are not offered.',
      section: 'Link dialog',
      context: 'link-dialog',
      i18n: {
        'de-AT': {
          title: 'Schlüssel anderer Projekte suchen',
          body: 'Sucht in Schlüsselnamen, Beschreibungen und dem Ausgangstext des Owners in jedem anderen Projekt, auch in Projekten, die Sie selbst nicht öffnen können, weil das Verknüpfen kein Recht im Owner-Projekt verlangt. Die Suche beginnt ab zwei Zeichen, sobald Sie zu tippen aufhören. Eigene Schlüssel dieses Projekts und Paketschlüssel werden nicht angeboten.',
        },
        'sr-Latn-RS': {
          title: 'Pretraga ključeva drugih projekata',
          body: 'Pretražuje imena ključeva, opise i izvorni tekst vlasnika u svakom drugom projektu, uključujući projekte koje sami ne možete da otvorite, jer povezivanje ne traži pravo u projektu-vlasniku. Pretraga počinje od dva znaka, kada prestanete da kucate. Sopstveni ključevi ovog projekta i ključevi paketa se ne nude.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-results"]',
      title: 'Read the results',
      body: 'Best match first: the exact name, then names that start with or contain the text, then descriptions, then the source text. Each row shows the key name, the project that owns it and its text in the owner’s source locale. “Load more” fetches the next 20.',
      section: 'Link dialog',
      context: 'link-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Ergebnisse lesen',
          body: 'Der beste Treffer zuerst: der genaue Name, dann Namen, die mit dem Text beginnen oder ihn enthalten, dann Beschreibungen, dann der Ausgangstext. Jede Zeile zeigt den Schlüsselnamen, das Projekt, dem er gehört, und seinen Text in der Ausgangssprache des Owners. „Load more“ lädt die nächsten 20.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje rezultata',
          body: 'Najbolji pogodak prvi: tačno ime, zatim imena koja počinju tekstom ili ga sadrže, zatim opisi, pa izvorni tekst. Svaki red pokazuje ime ključa, projekat kome pripada i njegov tekst na izvornom jeziku vlasnika. „Load more“ učitava sledećih 20.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-pick"]',
      title: 'Tick the keys to link',
      body: 'Tick a key to add it, tick again to drop it; the whole row can be clicked. A key this project links already is ticked and locked, marked “linked”. Your ticks stay while you search for something else.',
      section: 'Link dialog',
      context: 'link-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die zu verknüpfenden Schlüssel ankreuzen',
          body: 'Kreuzen Sie einen Schlüssel an, um ihn hinzuzufügen, und erneut, um ihn wieder zu entfernen; die ganze Zeile ist anklickbar. Ein Schlüssel, den dieses Projekt bereits verknüpft, ist angekreuzt, gesperrt und mit „linked“ markiert. Ihre Auswahl bleibt erhalten, während Sie nach etwas anderem suchen.',
        },
        'sr-Latn-RS': {
          title: 'Čekiranje ključeva za povezivanje',
          body: 'Čekirajte ključ da ga dodate, a ponovo da ga uklonite; ceo red može da se klikne. Ključ koji ovaj projekat već povezuje je čekiran, zaključan i označen sa „linked“. Vaš izbor ostaje dok tražite nešto drugo.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-picked"]',
      title: 'The picked keys',
      body: 'Every key ticked so far, from all searches. The × on a key drops it again.',
      section: 'Link dialog',
      context: 'link-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die ausgewählten Schlüssel',
          body: 'Alle bisher angekreuzten Schlüssel aus allen Suchen. Das × an einem Schlüssel entfernt ihn wieder.',
        },
        'sr-Latn-RS': {
          title: 'Izabrani ključevi',
          body: 'Svi do sada čekirani ključevi, iz svih pretraga. × na ključu ga ponovo uklanja.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-names"]',
      title: 'Paste the key names',
      body: 'One name per line (commas and spaces separate names too), exactly as the application asks for them; together with the picked keys at most 500 at a time. The owner is found from the key’s prefix, so the names alone are enough.',
      section: 'Link dialog',
      context: 'link-dialog-paste',
      i18n: {
        'de-AT': {
          title: 'Die Schlüsselnamen einfügen',
          body: 'Ein Name pro Zeile (auch Kommas und Leerzeichen trennen Namen), genau so, wie die Anwendung sie anfragt; zusammen mit den ausgewählten Schlüsseln höchstens 500 auf einmal. Der Owner ergibt sich aus dem Präfix des Schlüssels, die Namen allein genügen also.',
        },
        'sr-Latn-RS': {
          title: 'Lepljenje imena ključeva',
          body: 'Jedno ime po redu (imena razdvajaju i zarezi i razmaci), tačno onako kako ih aplikacija traži; zajedno sa izabranim ključevima najviše 500 odjednom. Vlasnik se nalazi po prefiksu ključa, pa su dovoljna sama imena.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-submit"]',
      title: 'Link them',
      body: 'Links every picked key and every pasted name that is a key of another project; the label counts them. The dialog closes when all of them were linked or already were. A name that cannot be linked fails on its own and the others still go through.',
      section: 'Link dialog',
      context: 'link-dialog',
      i18n: {
        'de-AT': {
          title: 'Verknüpfen',
          body: 'Verknüpft jeden ausgewählten Schlüssel und jeden eingefügten Namen, der ein Schlüssel eines anderen Projekts ist; die Beschriftung zählt sie. Der Dialog schließt sich, wenn alle verknüpft wurden oder es bereits waren. Ein Name, der sich nicht verknüpfen lässt, scheitert für sich allein, die anderen werden trotzdem verknüpft.',
        },
        'sr-Latn-RS': {
          title: 'Povezivanje',
          body: 'Povezuje svaki izabrani ključ i svako nalepljeno ime koje je ključ drugog projekta; natpis na dugmetu ih broji. Dijalog se zatvara kada su svi povezani ili su već bili. Ime koje ne može da se poveže ne uspeva samo, a ostala se svejedno povezuju.',
        },
      },
    },
    {
      selector: '[data-help="key-links-dialog-refused"]',
      title: 'Names that were not linked',
      body: 'A pasted name stays in the field and a picked key stays picked, each with the reason: no project has a key of that name; the key is this project’s own and in the export already; or a package owns it, and then the project subscribes to the package instead.',
      section: 'Link dialog',
      context: 'link-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Namen, die nicht verknüpft wurden',
          body: 'Ein eingefügter Name bleibt im Feld und ein ausgewählter Schlüssel bleibt ausgewählt, jeweils mit dem Grund: Kein Projekt hat einen Schlüssel mit diesem Namen; der Schlüssel gehört diesem Projekt selbst und ist bereits im Export; oder ein Paket besitzt ihn, und dann abonniert das Projekt stattdessen das Paket.',
        },
        'sr-Latn-RS': {
          title: 'Imena koja nisu povezana',
          body: 'Nalepljeno ime ostaje u polju, a izabrani ključ ostaje izabran, svako sa razlogom: nijedan projekat nema ključ tog imena; ključ je sopstveni ključ ovog projekta i već je u exportu; ili ga poseduje paket, pa se projekat umesto toga pretplaćuje na paket.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Verknüpfte Schlüssel',
      intro:
        'Schlüssel, die einem anderen Projekt gehören und die dieses Projekt ebenfalls exportiert. Wenn diese Anwendung einen Schlüssel anzeigt, der einer anderen Anwendung gehört, verknüpfen Sie ihn, statt seinen Text zu kopieren: Der Owner übersetzt ihn weiter, und das Bundle dieses Projekts enthält den Text des Owners.',
      sections: {
        'The list': 'Die Liste',
        Linking: 'Verknüpfen',
        'Link dialog': 'Verknüpfungsdialog',
      },
      contexts: {
        'link-dialog': 'Klicken Sie oben auf der Seite auf „Link keys“.',
        'link-dialog-paste':
          'Klicken Sie oben auf der Seite auf „Link keys“ und dann auf den Reiter „Paste names“.',
      },
      tips: [
        'Ein verknüpfter Schlüssel wird in den Sprachen ausgeliefert, die sein Owner führt, und erst, wenn der Owner einen Ausgangstext dafür hat. In einer Sprache, die der Owner nicht führt, greift in der Anwendung der Fallback wie bei jedem fehlenden Wert.',
        'Änderungen an einem verknüpften Schlüssel aus diesem Projekt heraus – im Schlüsselraster oder im In-Context-Editor – werden nur für Personen direkt geschrieben, die im Owner-Projekt übersetzen dürfen. Für alle anderen wird daraus ein Vorschlag, über den der Owner auf seiner Seite „Suggestions“ entscheidet.',
        'Paketschlüssel werden nicht verknüpft: Abonnieren Sie das Paket in den Projekteinstellungen. Einen Schlüssel, den der Eingang „Missing keys“ meldet, können Sie dort mit „Use from …“ verknüpfen.',
        'Der Owner sieht, welche Projekte einen Schlüssel verwenden, und das Umbenennen oder Löschen fragt zuerst nach einer Bestätigung, weil der Code dieser Projekte noch den alten Namen anfragt.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Povezani ključevi',
      intro:
        'Ključevi koji pripadaju drugom projektu, a koje i ovaj projekat izvozi. Kada ova aplikacija prikazuje ključ koji pripada drugoj aplikaciji, povežite ga umesto da kopirate njegov tekst: vlasnik ga i dalje prevodi, a bundle ovog projekta nosi tekst vlasnika.',
      sections: {
        'The list': 'Lista',
        Linking: 'Povezivanje',
        'Link dialog': 'Dijalog povezivanja',
      },
      contexts: {
        'link-dialog': 'Kliknite na „Link keys“ na vrhu stranice.',
        'link-dialog-paste': 'Kliknite na „Link keys“ na vrhu stranice, a zatim na tab „Paste names“.',
      },
      tips: [
        'Povezan ključ se isporučuje na jezicima koje vlasnik nosi, i tek kada vlasnik ima izvorni tekst za njega. Na jeziku koji vlasnik ne nosi aplikacija prelazi na rezervnu vrednost kao za svaku vrednost koja nedostaje.',
        'Izmene povezanog ključa napravljene u ovom projektu – u gridu ključeva ili u in-context editoru – upisuju se direktno samo za one koji smeju da prevode u projektu-vlasniku. Izmena svih ostalih postaje predlog o kome vlasnik odlučuje na svojoj stranici „Suggestions“.',
        'Ključevi paketa se ne povezuju: pretplatite se na paket u podešavanjima projekta. Ključ koji prijavi inbox „Missing keys“ može da se poveže odande dugmetom „Use from …“.',
        'Vlasnik vidi koji projekti koriste ključ, a preimenovanje ili brisanje prvo traži potvrdu, jer kod tih projekata i dalje traži staro ime.',
      ],
    },
  },
};
